import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { DataSourcesTypes } from '@frontend/api-data-sources';
import { PersonAPI, PersonTypes } from '@frontend/api-person';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useAlert, UsePopoverDialogResponse } from '@frontend/design-system';
import { invalidateFilterForPersonSearch } from '../../utils';
import { PersonSelector } from '../../views/Calendar/components/PersonSelector/PersonSelector';
import {
  PersonSelectorFormValues,
  PersonSelectorPropsType,
  PatientSelectorScreenEnum,
} from '../../views/Calendar/components/PersonSelector/types';
import { SourceTenantListItem } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestModal/types';
import { ScheduleRequestsTrackingIds } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/trackingIds';
import { convertToServerDate } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/utils';

type ReviewAppointmentLinkPatientProps = {
  access: DataSourcesTypes.AccessDataResponse;
  isIntegratedOffice: boolean;
  personSelectorProps: PersonSelectorPropsType;
  personSelectorDialogProps: UsePopoverDialogResponse<HTMLButtonElement, false, HTMLDialogElement>;
  selectedPerson: PersonTypes.Person | null;
  selectedScheduleRequest: ScheduleRequest;
  patientWritebackSourceTenants: SourceTenantListItem[];
};

export const ReviewAppointmentLinkPatient = ({
  isIntegratedOffice,
  personSelectorProps,
  personSelectorDialogProps,
  selectedPerson,
  selectedScheduleRequest,
  patientWritebackSourceTenants,
}: ReviewAppointmentLinkPatientProps) => {
  const queryClient = useQueryClient();
  const alert = useAlert();
  const { t } = useTranslation('scheduleCalendarRequest');

  const { mutateAsync: writebackPerson, isLoading: isLoadingAddWritebackCustomer } =
    SchedulerV3.Mutations.usePersonWriteBackMutation();

  const { mutateAsync: addCustomContact, isLoading: isAddingCustomContact } = ScheduleQueries.useMutateAddCustomContact(
    selectedScheduleRequest.locationId ?? ''
  );

  const handleAddCustomContact = async (formValues: PersonSelectorFormValues) => {
    return await addCustomContact({
      first_name: formValues?.firstName ?? '',
      last_name: formValues?.lastName ?? '',
      phone_mobile: formValues?.phoneNumber ?? '',
      birthdate: formValues?.dateOfBirth ? convertToServerDate(formValues?.dateOfBirth) : '',
      email: formValues?.email ?? '',
      gender: formValues?.gender ?? '',
    });
  };

  const handlePersonWriteback = async (formValues: PersonSelectorFormValues) => {
    return await writebackPerson({
      locationId: selectedScheduleRequest.locationId || '',
      sourceTenantId: formValues.source || '',
      firstName: formValues.firstName || '',
      lastName: formValues.lastName || '',
      email: formValues.email || '',
      gender: formValues.gender || '',
      mobilePhone: formValues.phoneNumber || '',
      birthdate: formValues.dateOfBirth ? dayjs(formValues.dateOfBirth).format() : '',
      address1: selectedScheduleRequest?.schedulee?.address?.street ?? '',
      address2: selectedScheduleRequest?.schedulee?.address?.unit ?? '',
      city: selectedScheduleRequest?.schedulee?.address?.city ?? '',
      state: selectedScheduleRequest?.schedulee?.address?.state ?? '',
      postalCode: selectedScheduleRequest?.schedulee?.address?.postalCode ?? '',
    });
  };

  const handleAddCustomerContact = async (formValues: PersonSelectorFormValues) => {
    try {
      const response = isIntegratedOffice
        ? await handlePersonWriteback(formValues)
        : await handleAddCustomContact(formValues);

      if (response) {
        // Fetch Person details and link to the appointment if person_id is returned from the create person API
        if ('data' in response && response.data.person_id) {
          try {
            const res = await PersonAPI.getPersonExtended(response?.data?.person_id);
            personSelectorProps.setSelectedPerson(res);
            personSelectorDialogProps.close();
          } catch (error) {
            alert.error(t('Error linking patient to appointment'));
            personSelectorProps.setPersonSelectorScreen(PatientSelectorScreenEnum.PERSON_SEARCH);
          }
        } else {
          personSelectorProps.setPersonSelectorScreen(PatientSelectorScreenEnum.PERSON_SEARCH);
        }

        alert.success(t('Customer successfully created.'));
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            invalidateFilterForPersonSearch(queryKey, formValues, selectedScheduleRequest.locationId),
        });
      }
    } catch (e) {
      alert.error(t('We were unable to create the customer.'));
    }
  };

  return (
    <>
      <PersonSelector
        shouldAddAndLink={!isIntegratedOffice}
        disableCustomContacts={isIntegratedOffice}
        addContactScreenTitle={t('Add Patient')}
        defaultSearchValue={selectedScheduleRequest?.schedulee?.phoneNumber ?? ''}
        locationId={selectedScheduleRequest.locationId ?? ''}
        handleAddCustomerContact={handleAddCustomerContact}
        patientWritebackSourceTenants={patientWritebackSourceTenants}
        isLoading={isLoadingAddWritebackCustomer || isAddingCustomContact}
        personSelectorDialogProps={personSelectorDialogProps}
        selectedPerson={selectedPerson}
        showDataSources={true}
        addNewButtonTrackingId={ScheduleRequestsTrackingIds.personSelectorAddNewPersonBtn}
        addToListButtonTrackingId={ScheduleRequestsTrackingIds.personSelectorAddToListBtn}
        cancelButtonTrackingId={ScheduleRequestsTrackingIds.personSelectorCancelBtn}
        {...personSelectorProps}
      />
    </>
  );
};
